// eslint-disable-next-line max-classes-per-file
import { autobind } from 'core-decorators';
import { tryCatch } from 'app/blocks/common/decorators';
import { getQueryParamSafe } from 'app/blocks/common/utils';
import { refreshUser } from 'app/blocks/middleware/authentication';
import { confirmRedirectToOrcid } from 'app/blocks/orcid/utils';
import { navigate } from 'app/route/history';
import OrcidConnector from './OrcidConnector';
import createImportMethod from './OrcidInfoImporter';

class OrcidNavigationManager {
    static ORCID_SECTION = 'orcid';

    static isOrchid() {
        const queryParams = getQueryParamSafe(window.location.href);
        return OrcidNavigationManager.isOrchidSection(queryParams.section);
    }

    static isOrchidSection(p) {
        return String(p).toLowerCase() === OrcidNavigationManager.ORCID_SECTION;
    }

    static needRedirect() {
        // Refresh the page without '?section=ORCID'
        const query = getQueryParamSafe(window.location.href);
        return OrcidNavigationManager.isOrchidSection(query.section);
    }

    static redirectWithoutOrcidQuery() {
        // Refresh the page without '?section=ORCID'
        const url = window.location.href;
        const query = getQueryParamSafe(url);
        if (OrcidNavigationManager.isOrchidSection(query.section)) {
            delete query.section;
            const queryString = Object.keys(query)
                .map(key => `${key}=${query[key]}`, [])
                .join('&');
            navigate(window.location.hash.replace('#', '').split('?')[0] + (queryString ? `?${queryString}` : ''));
        }
    }
}

@autobind
class ProfileOrcidConnector {
    connector: OrcidConnector;

    isSkipConfirmationDialog: boolean;

    constructor(isSkipConfirmationDialog) {
        this.connector = new OrcidConnector();
        this.isSkipConfirmationDialog = isSkipConfirmationDialog;
    }

    async startOrcid() {
        if (OrcidNavigationManager.isOrchid()) {
            return this.openOrcid();
        }
        return null;
    }

    @tryCatch.popUpOnly
    async openOrcid() {
        if (!this.isSkipConfirmationDialog && !(await confirmRedirectToOrcid())) {
            return;
        }

        const [orcidCode, options] = await this.connector.connect(true);

        // eslint-disable-next-line consistent-return
        return this.processResult(orcidCode, options);
    }

    // eslint-disable-next-line consistent-return
    async processResult(orcidCode, options) {
        const importOrcidInfo = createImportMethod(orcidCode, options);
        const { loggedIn } = await refreshUser();

        if (options.shouldInvokeCallback) {
            if (OrcidNavigationManager.needRedirect()) {
                await importOrcidInfo();
                OrcidNavigationManager.redirectWithoutOrcidQuery();
            } else {
                return importOrcidInfo();
            }
        }

        navigate(loggedIn ? 'profile' : 'register', { replace: true });
    }
}

const TestKit = {
    OrcidNavigationManagerClass: OrcidNavigationManager,
};

export { TestKit };
export default ProfileOrcidConnector;
